import { Injectable } from '@angular/core';
import Fuse from 'fuse.js';
import type { IFuseOptions } from 'fuse.js';
import { HelpArticle } from '../../core/models/help-content.model';
import { HelpContentService } from './help-content.service';

export interface HelpSearchHit {
  article: HelpArticle;
  score: number;
}

type AudienceKey = 'kunden' | 'handwerker';

/**
 * Fuse.js-basierte Volltextsuche über die FAQ-Datenbank.
 * - Index wird lazy je Audience aufgebaut.
 * - Felder mit Gewichtung gemäß OnePager § 6: question (×3), keywords (×2),
 *   summary (×1).
 */
@Injectable({ providedIn: 'root' })
export class HelpSearchService {
  private readonly indexes = new Map<AudienceKey, Fuse<HelpArticle>>();

  constructor(private readonly content: HelpContentService) {}

  search(audience: AudienceKey, query: string, max = 8): HelpSearchHit[] {
    const q = (query ?? '').trim();
    if (q.length < 2) return [];
    const fuse = this.getIndex(audience);
    return fuse
      .search(q, { limit: max })
      .map((r) => ({ article: r.item, score: r.score ?? 1 }));
  }

  private getIndex(audience: AudienceKey): Fuse<HelpArticle> {
    const cached = this.indexes.get(audience);
    if (cached) return cached;

    const articles = this.content.getAudience(audience).articles;
    const options: IFuseOptions<HelpArticle> = {
      keys: [
        { name: 'question', weight: 3 },
        { name: 'keywords', weight: 2 },
        { name: 'summary', weight: 1 },
      ],
      threshold: 0.4,
      ignoreLocation: true,
      includeScore: true,
      minMatchCharLength: 2,
    };
    const fuse = new Fuse(articles, options);
    this.indexes.set(audience, fuse);
    return fuse;
  }
}
